import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { auth } from '../lib/api';
import { useContent } from '../context/ContentContext';
import { useToast } from '../hooks/use-toast';
import ProductsManager from '../components/admin/ProductsManager';
import TestimonialsManager from '../components/admin/TestimonialsManager';
import HeroManager from '../components/admin/HeroManager';
import GalleryManager from '../components/admin/GalleryManager';
import CompanyManager from '../components/admin/CompanyManager';
import SiteContentManager from '../components/admin/SiteContentManager';
import SectionsManager from '../components/admin/SectionsManager';
import {
  LogOut, ExternalLink, Inbox, Package, Star, ImagePlus, Layers, Building2, FileText,
  ChevronRight, Sliders,
} from 'lucide-react';

const TABS = [
  { key: 'enquiries', label: 'Enquiries', icon: Inbox, link: '/admin' },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'testimonials', label: 'Testimonials', icon: Star },
  { key: 'hero', label: 'Hero Banners', icon: ImagePlus },
  { key: 'gallery', label: 'Gallery', icon: Layers },
  { key: 'company', label: 'Company Info', icon: Building2 },
  { key: 'content', label: 'Page Content', icon: FileText },
  { key: 'sections', label: 'Site Sections & Theme', icon: Sliders },
];

export default function AdminCMS() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh: refreshPublic } = useContent();
  const [activeTab, setActiveTab] = useState('products');

  if (!auth.isAuthenticated()) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => { auth.logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="admin-cms-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f7941e] to-[#e67e00] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display uppercase text-lg tracking-wide text-gray-900">Content Manager</h1>
              <p className="text-[11px] text-gray-500">Ess Bee Heaters Pvt. Ltd.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#f7941e] px-3 py-2" data-testid="view-site-link">
              <ExternalLink className="w-4 h-4" /> View site
            </Link>
            <button data-testid="logout-btn" onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-20 self-start">
          <nav className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {TABS.map(t => (
              t.link ? (
                <Link key={t.key} to={t.link} data-testid={`tab-${t.key}`} className="flex items-center gap-2 px-4 py-3 text-sm border-b last:border-b-0 border-gray-100 hover:bg-gray-50 text-gray-700">
                  <t.icon className="w-4 h-4 text-gray-400" />
                  {t.label}
                  <ChevronRight className="w-4 h-4 ml-auto text-gray-300" />
                </Link>
              ) : (
                <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setActiveTab(t.key)}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-sm border-b last:border-b-0 border-gray-100 text-left transition-colors ${activeTab === t.key ? 'bg-[#fff7e6] text-[#e67e00] font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}>
                  <t.icon className={`w-4 h-4 ${activeTab === t.key ? 'text-[#f7941e]' : 'text-gray-400'}`} />
                  {t.label}
                </button>
              )
            ))}
          </nav>
        </aside>

        {/* Main panel */}
        <main className="min-w-0">
          {activeTab === 'products' && <ProductsManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'testimonials' && <TestimonialsManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'hero' && <HeroManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'gallery' && <GalleryManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'company' && <CompanyManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'content' && <SiteContentManager onSaved={refreshPublic} toast={toast} />}
          {activeTab === 'sections' && <SectionsManager onSaved={refreshPublic} toast={toast} />}
        </main>
      </div>
    </div>
  );
}
